import { ReactNode } from 'react';
import { useToast as useToastCK } from '@chakra-ui/react';
import BasicToast from 'components/widgets/toast/BasicToast';
import LoadingToast from 'components/widgets/toast/LoadingToast';
import UndoToast from 'components/widgets/toast/UndoToast';
import { trackErrorToast } from 'utils/analytics';

/** Chakra v2 toast render callback props */
type ToastRenderProps = { onClose: () => void };

type TStatusType = 'success' | 'error' | 'warning';

type TBasicToastProps = {
  toastTitle: string;
  status?: TStatusType;
  title?: string;
  duration?: number | null;
  label: string;
};

type TUndoToastProps = {
  toastTitle: string;
  onClickUndo: () => void;
  children: ReactNode;
};

type TLoadingToastProps = {
  toastTitle: string;
  status?: TStatusType;
  label: string;
  duration?: number;
};

type TCustomToastProps = {
  title: string;
  description?: string;
  status: TStatusType;
  duration?: number | null;
  isClosable?: boolean;
  render?: (props: ToastRenderProps) => ReactNode;
};

export const useToast = () => {
  const toast = useToastCK();

  const closeToast = (id: string) => {
    toast.close(id);
  };

  const closeAllToasts = () => {
    toast.closeAll();
  };

  const renderToast = ({
    toastTitle,
    status = 'success',
    label,
    title,
    duration = 5000,
  }: TBasicToastProps) => {
    if (status === 'error') {
      trackErrorToast({
        errorMessage: [title, label]
          .filter((item): item is string => item !== undefined)
          .join(' '),
      });
    }

    toast({
      title: toastTitle,
      duration,
      isClosable: true,
      render: ({ onClose }) => (
        <BasicToast status={status} onClose={onClose} label={label} title={title} />
      ),
    });
  };

  const renderUndoToast = ({ toastTitle, onClickUndo, children }: TUndoToastProps) => {
    toast({
      title: toastTitle,
      duration: 5000,
      isClosable: true,
      render: ({ onClose }) => (
        <UndoToast
          onClickUndo={() => {
            onClickUndo();
            onClose();
          }}
          onClose={onClose}
        >
          {children}
        </UndoToast>
      ),
    });
  };

  const renderLoadingToast = ({
    toastTitle,
    status = 'success',
    label,
  }: TLoadingToastProps) => {
    const id = `loading-${toastTitle}-${status}`;
    if (!toast.isActive(id)) {
      toast({
        id,
        title: toastTitle,
        duration: null, // Indefinite duration; we'll close it manually
        isClosable: true,
        render: () => <LoadingToast label={label} />,
      });
    }

    return id; // Return the unique ID
  };

  // Wraps toast from Chakra UI with MercatorAI specific logic
  const renderCustomToast = (props: TCustomToastProps) => {
    if (props.status === 'error') {
      trackErrorToast({
        errorMessage: [props.title, props.description]
          .filter((item): item is string => item !== undefined)
          .join(' '),
      });
    }
    toast(props);
  };

  return {
    renderToast,
    renderUndoToast,
    renderLoadingToast,
    renderCustomToast,
    closeToast,
    closeAllToasts,
  };
};
